/**
 * Office job-page parts card (PARTS-CUSTODY.md §V2 item 3c): each tracked
 * part on the job with its current state chip inline, so nobody opens the
 * custody page to answer "did the kits make it onto the truck?". Received
 * parts that aren't staged show the amber "not staged" chip; Stage is one
 * tap right here. Renders nothing while the job has no tracked parts.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { PartStatePill, PartTimelineDrawer } from "@/components/parts/part-timeline-drawer";
import { StagePartPopover } from "@/components/parts/stage-part-popover";
import { toast } from "sonner";
import { Package, Boxes, CheckCircle2, History, Link2 } from "lucide-react";

interface JobTrackedPart {
    id: string;
    short_code: string;
    description: string;
    unit_cost: number;
    custody_state: string;
    holder_name: string | null;
    staged_note: string | null;
    po_number: string | null;
    days_in_state: number;
    job_id: string | null;
    job_number: string | null;
    job_title: string | null;
    customer_name: string | null;
    flagged_reason: string | null;
}

// #829: the job's PO lines that never became tracked parts — real physical
// objects that only live on the pull sheet (below the tracking threshold).
interface PullSheetLine {
    po_line_id: string;
    part_name: string;
    quantity: number;
    received_qty: number;
    is_fully_received: boolean;
    unit_cost: number;
    po_number: string | null;
    po_status: string;
}

interface JobPartsResponse {
    parts: JobTrackedPart[];
    pull_sheet_lines?: PullSheetLine[];
    threshold_cents?: number;
}

export function JobPartsCard({ jobId, canManage }: { jobId: string; canManage: boolean }) {
    const qc = useQueryClient();
    const [timelinePart, setTimelinePart] = useState<JobTrackedPart | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const { data } = useQuery({
        queryKey: ["job-parts", jobId],
        queryFn: () => api.get<JobPartsResponse>(`/api/tracked-parts/for-job/${jobId}`),
        staleTime: 30_000,
    });

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ["job-parts", jobId] });
        void qc.invalidateQueries({ queryKey: ["tracked-parts"] });
    };
    // #818: attach an already-tracked, unlinked part to THIS job — the path
    // that didn't exist (the endpoint had zero callers, so an ad-hoc part or
    // an unlinked-PO part could never reach the tech's Parts tab).
    const attach = useMutation({
        mutationFn: (partId: string) => api.post(`/api/tracked-parts/${partId}/attach`, { jobId }),
        onSuccess: () => {
            toast.success("Part linked to this job.");
            void qc.invalidateQueries({ queryKey: ["unattached-parts"] });
            invalidate();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not link the part"),
    });
    const stage = useMutation({
        mutationFn: ({ partId, note }: { partId: string; note?: string }) =>
            api.post(`/api/tracked-parts/${partId}/stage`, { note }),
        onSuccess: () => {
            toast.success("Staged for pickup.");
            invalidate();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not stage the part"),
    });
    const checkIn = useMutation({
        mutationFn: (partId: string) => api.post(`/api/tracked-parts/${partId}/check-in`, {}),
        onSuccess: () => {
            toast.success("Checked in — signed off.");
            invalidate();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Check-in failed"),
    });

    const parts = data?.parts ?? [];
    const pullLines = data?.pull_sheet_lines ?? [];
    // #829: count PHYSICAL OBJECTS, not custody rows — the audit found a job
    // panel saying "1 part not staged" on a PO with two real objects.
    const pullUnits = pullLines.reduce((n, l) => n + Math.max(1, Math.round(l.quantity)), 0);
    const totalObjects = parts.length + pullUnits;
    const thresholdDollars =
        data?.threshold_cents != null && data.threshold_cents > 0 ? Math.round(data.threshold_cents / 100) : null;
    // #818: the card used to vanish on 0 parts, which also hid the only place
    // the office could have linked the FIRST part. Managers keep the card
    // (with an honest empty line + Add existing part); read-only viewers keep
    // the quiet null (unless pull-sheet lines exist — those are real parts).
    if (parts.length === 0 && pullLines.length === 0 && !canManage) return null;
    const notStaged = parts.filter((p) => p.custody_state === "received").length;

    return (
        <>
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
                        <Package className="h-4 w-4" /> Parts for this job
                        {totalObjects > 0 && (
                            <span
                                className="text-xs font-normal text-muted-foreground"
                                data-testid="parts-object-count"
                            >
                                {totalObjects} part{totalObjects === 1 ? "" : "s"} · {parts.length} tracked
                                {notStaged > 0 ? ` (${notStaged} not staged)` : ""}
                                {pullUnits > 0 ? ` · ${pullUnits} on the pull sheet` : ""}
                            </span>
                        )}
                        {notStaged > 0 && (
                            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-black">
                                {notStaged} part{notStaged === 1 ? "" : "s"} not staged
                            </span>
                        )}
                        {canManage && (
                            <button
                                className="ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium hover:bg-muted"
                                onClick={() => setAddOpen(true)}
                                title="Link an already-tracked part (ad-hoc or from an unlinked PO) to this job"
                            >
                                <Link2 className="h-3.5 w-3.5" /> Add existing part
                            </button>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {parts.length === 0 && pullLines.length === 0 && (
                        <p className="text-xs text-muted-foreground" data-testid="job-parts-empty">
                            No parts on this job yet — nothing was ordered on a PO for it. Tracked parts can be linked
                            with <span className="font-medium">Add existing part</span>.
                        </p>
                    )}
                    {parts.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium">
                                    <span className="font-mono">{p.short_code}</span> · {p.description}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                    {p.unit_cost > 0 ? <span>${Math.round(p.unit_cost)}</span> : null}
                                    <PartStatePill state={p.custody_state} stagedNote={p.staged_note} />
                                    {p.holder_name && <span>{p.holder_name}</span>}
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                                {canManage && p.custody_state === "received" && (
                                    <StagePartPopover
                                        partCode={p.short_code}
                                        jobLabel="this job"
                                        currentNote={p.staged_note}
                                        pending={stage.isPending}
                                        onStage={(note) => stage.mutate({ partId: p.id, note })}
                                        trigger={
                                            <button
                                                className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-xs font-medium text-white hover:opacity-90"
                                                disabled={stage.isPending}
                                            >
                                                <Boxes className="h-3.5 w-3.5" /> Stage
                                            </button>
                                        }
                                    />
                                )}
                                {canManage &&
                                    (p.custody_state === "returning" || p.custody_state === "returning_to_vendor") && (
                                        <button
                                            className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
                                            disabled={checkIn.isPending}
                                            onClick={() => checkIn.mutate(p.id)}
                                        >
                                            <CheckCircle2 className="h-3.5 w-3.5" /> Check In
                                        </button>
                                    )}
                                <button
                                    className="rounded-md border p-1 hover:bg-muted"
                                    title="Part timeline"
                                    onClick={() => setTimelinePart(p)}
                                >
                                    <History className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                    {/* #829: the physical objects custody DOESN'T follow — a
                        $86.75 air gap is still a real thing that has to be on
                        a truck. Shown from the pull sheet's source of truth. */}
                    {pullLines.map((l) => (
                        <div
                            key={l.po_line_id}
                            data-testid="pull-sheet-line"
                            className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2"
                        >
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium">
                                    {l.part_name}
                                    {l.quantity > 1 ? ` ×${Math.round(l.quantity)}` : ""}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                    {l.unit_cost > 0 ? <span>${Math.round(l.unit_cost)}</span> : null}
                                    <span
                                        className="rounded-full border px-2 py-0.5 font-medium"
                                        title="Below the tracking threshold — this part rides the daily pull sheet instead of one-by-one custody"
                                    >
                                        from the pull sheet — not tracked individually
                                    </span>
                                    <span>
                                        {l.is_fully_received
                                            ? "received"
                                            : l.received_qty > 0
                                              ? `${Math.round(l.received_qty)} of ${Math.round(l.quantity)} in`
                                              : "on order"}
                                    </span>
                                    {l.po_number && <span>· {l.po_number}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                    {(pullLines.length > 0 || thresholdDollars != null) && (
                        <p className="text-[11px] text-muted-foreground" data-testid="threshold-note">
                            {thresholdDollars != null
                                ? `Parts $${thresholdDollars} and up are tracked one-by-one in Parts Custody; cheaper parts ride the daily pull sheet.`
                                : "High-value parts are tracked one-by-one in Parts Custody; cheaper parts ride the daily pull sheet."}
                        </p>
                    )}
                </CardContent>
            </Card>
            {timelinePart && <PartTimelineDrawer part={timelinePart} onClose={() => setTimelinePart(null)} />}
            {addOpen && (
                <AddExistingPartDialog
                    pending={attach.isPending}
                    onClose={() => setAddOpen(false)}
                    onPick={(partId) => {
                        attach.mutate(partId);
                        setAddOpen(false);
                    }}
                />
            )}
        </>
    );
}

// #818: picker over open-custody parts that have NO job — exactly the ones
// the audit found stranded (ad-hoc rows, unlinked-PO rows).
function AddExistingPartDialog({
    pending,
    onClose,
    onPick,
}: {
    pending: boolean;
    onClose: () => void;
    onPick: (partId: string) => void;
}) {
    const { data, isLoading } = useQuery({
        queryKey: ["unattached-parts"],
        queryFn: () => api.get<{ parts: JobTrackedPart[] }>(`/api/tracked-parts?unattached=1`),
    });
    const parts = data?.parts ?? [];
    return (
        <Dialog open onClose={onClose} title="Add an existing part to this job" className="max-w-md">
            <div className="space-y-2">
                <p className="text-xs text-foreground/70">
                    Tracked parts that aren&apos;t linked to any job. Linking one puts it on this job&apos;s page and
                    the assigned tech&apos;s Parts tab.
                </p>
                <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="unattached-parts-list">
                    {isLoading ? (
                        <p className="px-1 py-3 text-xs text-foreground/55">Loading…</p>
                    ) : parts.length === 0 ? (
                        <p className="px-1 py-3 text-xs text-foreground/55">
                            Every tracked part is already linked to a job. Track a new one from Parts Custody.
                        </p>
                    ) : (
                        parts.map((p) => (
                            <div
                                key={p.id}
                                className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
                            >
                                <div className="min-w-0">
                                    <div className="truncate font-medium">
                                        <span className="font-mono">{p.short_code}</span> · {p.description}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {p.unit_cost > 0 ? `$${Math.round(p.unit_cost)} · ` : ""}
                                        {p.custody_state.replace(/_/g, " ")}
                                        {p.po_number ? ` · ${p.po_number}` : ""}
                                    </div>
                                </div>
                                <button
                                    className="shrink-0 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                                    disabled={pending}
                                    onClick={() => onPick(p.id)}
                                >
                                    Link
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </Dialog>
    );
}
